import React, { useState } from "react";
import { Table, Spinner } from "reactstrap";
import { connect } from "react-redux";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import useValueBreakdownDetails from "../query/hooks/useValueBreakdownDetails";

const ValueDriversTable = ({ channels, markets, rights, selectedCurrency }) => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [perPage] = useState(50);
  const [sortBy, setSortBy] = useState("date");
  const [sortOrder, setSortOrder] = useState("desc");

  const { data, isFetching } = useValueBreakdownDetails({
    channels,
    markets,
    rights,
    page,
    perPage,
    sortBy,
    sortOrder,
  });

  const tableData = data?.data || [];
  const meta = data?.meta || { total: 0, current_page: 1, last_page: 1 };
  const filters = data?.filters || { channels: [], markets: [], rights: [] };

  // Don't show table if no filters are selected
  if (channels.length === 0 && markets.length === 0 && rights.length === 0) {
    return null;
  }

  const formatCurrency = (value) => {
    if (!value || !selectedCurrency?.value) return "0";

    const convertedValue = Math.round(value * selectedCurrency.value);
    const symbol = selectedCurrency.currency || "";

    return new Intl.NumberFormat("no-NO").format(convertedValue) + " " + symbol;
  };

  const formatNumber = (value) => {
    if (!value) return "0";
    return new Intl.NumberFormat("no-NO").format(value);
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortOrder("desc");
    }
    setPage(1);
  };

  const renderSortIcon = (column) => {
    if (sortBy !== column) return null;
    return (
      <i
        className={`mdi mdi-chevron-${sortOrder === "asc" ? "up" : "down"} ml-1`}
        style={{ fontSize: "14px" }}
      ></i>
    );
  };

  // Determine which columns to show based on data
  const hasChannel = tableData.some((row) => row.channel);
  const hasPlatform = tableData.some((row) => row.platform);
  const hasMarket = tableData.some((row) => row.market);
  // Check if sponsor_object or rights_holder columns exist in any row (even if null)
  // Use 'in' operator to check for property existence, not value
  const hasSponsorObject = tableData.some((row) => 'sponsor_object' in row && row.sponsor_object !== null);
  const hasRightsHolder = tableData.some((row) => 'rights_holder' in row && row.rights_holder !== null);
  const hasViews = tableData.some((row) => row.views !== null);
  const hasEngagements = tableData.some((row) => row.engagements !== null);
  const hasDuration = tableData.some((row) => row.duration_seconds !== null);
  const hasEngagementValue = tableData.some((row) => row.engagement_value !== null);

  // Build title based on active filters
  const buildTitle = () => {
    const parts = [];
    if (filters.channels.length > 0) {
      parts.push(filters.channels.join(", "));
    }
    if (filters.rights.length > 0) {
      parts.push(`by ${filters.rights.join(", ")}`);
    }
    if (filters.markets.length > 0) {
      parts.push(`in ${filters.markets.join(", ")}`);
    }
    return parts.length > 0 ? `${t("value_drivers")}: ${parts.join(" ")}` : t("value_drivers");
  };

  return (
    <div style={{ position: "relative", minHeight: "200px" }}>
      {/* Title */}
      <h6
        className="text-uppercase mb-3"
        style={{ color: "var(--text-secondary)", letterSpacing: "1px", fontSize: "13px" }}
      >
        {buildTitle()}
      </h6>

      {/* Loading overlay */}
      {isFetching && (
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 10,
          }}
        >
          <Spinner color="primary" />
        </div>
      )}

      {/* Table */}
      <div style={{ opacity: isFetching ? 0.5 : 1, transition: "opacity 0.3s", overflowX: "auto" }}>
        <Table responsive size="sm" style={{ fontSize: "13px" }}>
          <thead>
            <tr style={{ borderBottom: "2px solid var(--border-color)" }}>
              <th
                style={{ cursor: "pointer", color: "var(--text-secondary)" }}
                onClick={() => handleSort("date")}
              >
                {t("date")} {renderSortIcon("date")}
              </th>
              {hasChannel && (
                <th
                  style={{ cursor: "pointer", color: "var(--text-secondary)" }}
                  onClick={() => handleSort("channel")}
                >
                  {t("channel_type")} {renderSortIcon("channel")}
                </th>
              )}
              <th
                style={{ cursor: "pointer", color: "var(--text-secondary)" }}
                onClick={() => handleSort("source")}
              >
                {t("source")} {renderSortIcon("source")}
              </th>
              {hasPlatform && (
                <th style={{ color: "var(--text-secondary)" }}>{t("platform")}</th>
              )}
              {hasMarket && (
                <th style={{ color: "var(--text-secondary)" }}>{t("market")}</th>
              )}
              {hasSponsorObject && (
                <th style={{ color: "var(--text-secondary)" }}>{t("sponsor_object")}</th>
              )}
              {hasRightsHolder && (
                <th style={{ color: "var(--text-secondary)" }}>{t("rights_holder")}</th>
              )}
              <th style={{ color: "var(--text-secondary)" }}>{t("type")}</th>
              <th style={{ color: "var(--text-secondary)", minWidth: "200px" }}>{t("title")}</th>
              {hasViews && (
                <th
                  style={{ cursor: "pointer", color: "var(--text-secondary)", textAlign: "right" }}
                  onClick={() => handleSort("value")}
                >
                  {t("views_reach")} {renderSortIcon("value")}
                </th>
              )}
              {hasEngagements && (
                <th
                  style={{ cursor: "pointer", color: "var(--text-secondary)", textAlign: "right" }}
                  onClick={() => handleSort("engagement")}
                >
                  {t("engagements")} {renderSortIcon("engagement")}
                </th>
              )}
              {hasDuration && (
                <th style={{ color: "var(--text-secondary)", textAlign: "right" }}>
                  {t("duration")}
                </th>
              )}
              <th style={{ color: "var(--text-secondary)", textAlign: "right" }}>
                {t("media_value")}
              </th>
              {hasEngagementValue && (
                <th style={{ color: "var(--text-secondary)", textAlign: "right" }}>
                  {t("engagement_value")}
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {tableData.map((row) => (
              <tr key={row.id} style={{ color: "var(--text-primary)" }}>
                <td>{row.date ? format(new Date(row.date), "dd/MM/yyyy") : "-"}</td>
                {hasChannel && <td>{row.channel ? t(row.channel) : "-"}</td>}
                <td>{row.source || "-"}</td>
                {hasPlatform && <td>{row.platform || "-"}</td>}
                {hasMarket && (
                  <td>
                    {row.market_emoji && `${row.market_emoji} `}
                    {row.market || "-"}
                  </td>
                )}
                {hasSponsorObject && <td>{row.sponsor_object || "-"}</td>}
                {hasRightsHolder && <td>{row.rights_holder || "-"}</td>}
                <td>{row.type || "-"}</td>
                <td style={{ maxWidth: "300px" }}>
                  {row.permalink ? (
                    <a
                      href={row.permalink}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: "var(--text-primary)", textDecoration: "underline" }}
                    >
                      {row.title || "-"}
                    </a>
                  ) : (
                    row.title || "-"
                  )}
                </td>
                {hasViews && <td style={{ textAlign: "right" }}>{formatNumber(row.views)}</td>}
                {hasEngagements && (
                  <td style={{ textAlign: "right" }}>{formatNumber(row.engagements)}</td>
                )}
                {hasDuration && (
                  <td style={{ textAlign: "right" }}>
                    {row.duration_seconds ? `${row.duration_seconds}s` : "-"}
                  </td>
                )}
                <td style={{ textAlign: "right" }}>{formatCurrency(row.media_value)}</td>
                {hasEngagementValue && (
                  <td style={{ textAlign: "right" }}>{formatCurrency(row.engagement_value)}</td>
                )}
              </tr>
            ))}
          </tbody>
        </Table>

        {tableData.length === 0 && !isFetching && (
          <div className="text-center py-5" style={{ color: "var(--text-secondary)" }}>
            {t("no_data_available")}
          </div>
        )}

        {/* Pagination */}
        {meta.last_page > 1 && (
          <div className="d-flex justify-content-between align-items-center mt-3">
            <div style={{ color: "var(--text-secondary)", fontSize: "12px" }}>
              {t("showing")} {(meta.current_page - 1) * perPage + 1}-
              {Math.min(meta.current_page * perPage, meta.total)} {t("of")} {meta.total}
            </div>
            <div className="d-flex" style={{ gap: "5px" }}>
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                <i className="mdi mdi-chevron-left"></i>
              </button>
              <span
                style={{
                  padding: "5px 12px",
                  color: "var(--text-primary)",
                  fontSize: "12px",
                }}
              >
                {meta.current_page} / {meta.last_page}
              </span>
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={() => setPage((p) => Math.min(meta.last_page, p + 1))}
                disabled={page === meta.last_page}
              >
                <i className="mdi mdi-chevron-right"></i>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  selectedCurrency: state.currency.selectedCurrency,
});

export default connect(mapStateToProps)(ValueDriversTable);
